//AdminExpensesManager.js - Version finale avec gestion des anciennes dépenses
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AdminExpensesManager.css";

function AdminExpensesManager() {
  // États originaux
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [editingExpense, setEditingExpense] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [editingRemainingAmount, setEditingRemainingAmount] = useState(null);
  const [tempRemainingAmount, setTempRemainingAmount] = useState("");

  // États pour les locations
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [newLocationName, setNewLocationName] = useState("");
  const [showAddLocation, setShowAddLocation] = useState(false);

  // Configuration axios avec authentification
  const axiosInstance = axios.create({
    baseURL: "/",
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });

  // Formulaires avec location
  const [newExpense, setNewExpense] = useState({
    name: "",
    category: "divers",
    location: "",
    amount: "",
    priority: 3,
    isRecurring: false,
    recurrence: "monthly",
    notes: ""
  });

  const [newSupplierExpense, setNewSupplierExpense] = useState({
    supplierId: "",
    location: "",
    amount: "",
    priority: 3,
    notes: "",
    remainingAmount: ""
  });

  // 🔥 FILTRER LES DÉPENSES POUR LES DEUX TABLEAUX
  const getNormalExpenses = () => {
    return expenses.filter(expense => {
      const isNormal = (!expense.supplierId || expense.supplierId === "" || expense.supplierId === null);

      // Filtrer par location si sélectionnée
      if (selectedLocation && selectedLocation !== "all") {
        const expenseLocationId = expense.locationId || expense.location;
        return isNormal && expenseLocationId === selectedLocation;
      }

      return isNormal;
    });
  };

  const getSupplierExpenses = () => {
    return expenses.filter(expense => {
      const isSupplier = expense.supplierId && expense.supplierId.trim() !== "";

      // Filtrer par location si sélectionnée
      if (selectedLocation && selectedLocation !== "all") {
        const expenseLocationId = expense.locationId || expense.location;
        return isSupplier && expenseLocationId === selectedLocation;
      }

      return isSupplier;
    });
  };

  // 🔥 CHARGER LES CATÉGORIES
  const loadCategories = async () => {
    try {
      const response = await axiosInstance.get("/api/Expenses/getAllCategories");
      if (response.data.success) {
        // Éviter les doublons
        const uniqueCategories = [];
        const seen = new Set();

        response.data.categories.forEach(cat => {
          if (!seen.has(cat.name)) {
            seen.add(cat.name);
            uniqueCategories.push(cat);
          }
        });

        setCategories(uniqueCategories);
      }
    } catch (err) {
      console.error("❌ Erreur chargement catégories:", err);
      setCategories(getDefaultCategories());
    }
  };

  const getDefaultCategories = () => {
    return [
      { name: "salaires", label: "Salaires", color: "#3498db", isDefault: true },
      { name: "steg", label: "STEG", color: "#f39c12", isDefault: true },
      { name: "sonede", label: "SONEDE", color: "#3498db", isDefault: true },
      { name: "amenagement", label: "Aménagement", color: "#9b59b6", isDefault: true },
      { name: "glovo", label: "Glovo", color: "#00b894", isDefault: true },
      { name: "yassir", label: "Yassir", color: "#0984e3", isDefault: true },
      { name: "loyer", label: "Loyer", color: "#d63031", isDefault: true },
      { name: "fournitures", label: "Fournitures", color: "#00cec9", isDefault: true },
      { name: "marketing", label: "Marketing", color: "#fd79a8", isDefault: true },
      { name: "entretien", label: "Entretien", color: "#636e72", isDefault: true },
      { name: "divers", label: "Divers", color: "#b2bec3", isDefault: true }
    ];
  };

  // 🔥 CHARGER LES LOCATIONS
  const loadLocations = async () => {
    try {
      console.log("🔄 Chargement des locations...");
      const response = await axiosInstance.get("/api/Locationspin/getAllLocations");
      console.log("📥 Réponse locations:", response.data);

      if (response.data) {
        const locationsWithDetails = response.data.map(loc => ({
          _id: loc._id,
          name: loc.name,
          ...loc
        }));
        setLocations(locationsWithDetails);

        // Définir une location par défaut si disponible
        if (locationsWithDetails.length > 0) {
          const defaultLocation = locationsWithDetails[0]._id;
          if (!newExpense.location) {
            setNewExpense(prev => ({ ...prev, location: defaultLocation }));
          }
          if (!newSupplierExpense.location) {
            setNewSupplierExpense(prev => ({ ...prev, location: defaultLocation }));
          }
        }
      }
    } catch (err) {
      console.error("❌ Erreur chargement locations:", err);
      // Location par défaut pour développement
      const defaultLocations = [{ _id: "default", name: "Siège Principal" }];
      setLocations(defaultLocations);
      setNewExpense(prev => ({ ...prev, location: "default" }));
      setNewSupplierExpense(prev => ({ ...prev, location: "default" }));
    }
  };

  // 🔥 CHARGER LES FOURNISSEURS
  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const response = await axiosInstance.get("/api/Suppliers/getSuppliers");
      if (response.data && response.data.suppliers) {
        setSuppliers(response.data.suppliers);
      }
    } catch (err) {
      console.error("❌ Erreur chargement fournisseurs:", err);
      setSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  // 🔥 CHARGER TOUTES LES DÉPENSES - VERSION AMÉLIORÉE POUR DIAGNOSTIC
  const loadAllExpenses = async (locationsList = null, suppliersList = null) => {
    // Utiliser locationsList/suppliersList si fournis, sinon utiliser le state
    const currentLocations = locationsList || locations;
    const currentSuppliers = suppliersList || suppliers;
    try {
      setLoading(true);
      setError("");

      // Construire les paramètres de requête
      const params = {};
      if (selectedLocation && selectedLocation !== "all") {
        params.location = selectedLocation;
      }

      console.log("🔄 Chargement dépenses avec params:", params);

      const response = await axiosInstance.get("/api/Expenses/getAllExpenses", { params });

      if (response.data.success) {
        console.log("📥 Dépenses reçues du backend:", response.data.expenses);

        // Traiter les locations dans les dépenses avec le nouveau format du backend
        const processedExpenses = response.data.expenses.map(expense => {
          let locationName = "Non spécifiée";
          let locationId = null;

          // Le backend peut maintenant retourner location comme objet ou comme ID
          if (expense.location) {
            // Cas 1: location est un objet peuplé (avec _id et name)
            if (typeof expense.location === 'object' && expense.location._id) {
              locationId = expense.location._id;
              locationName = expense.location.name || "Sans nom";
            }
            // Cas 2: location est un ID string
            else if (typeof expense.location === 'string') {
              locationId = expense.location;
              const locationObj = currentLocations.find(loc => loc._id === expense.location);
              locationName = locationObj ? locationObj.name : "Location inconnue";
            }
          }
          // Si le backend a déjà fourni locationId et locationName
          else if (expense.locationId && expense.locationName) {
            locationId = expense.locationId;
            locationName = expense.locationName;
          }
          // Pour les anciennes dépenses sans location, mettre une location par défaut
          else {
            locationName = "Ancienne dépense (pas de location)";
          }

          // Traiter le fournisseur
          let supplierName = "Non spécifié";
          let supplierIdValue = null;

          if (expense.supplierId) {
            // Cas 1: supplierId est un objet peuplé
            if (typeof expense.supplierId === 'object' && expense.supplierId._id) {
              supplierIdValue = expense.supplierId._id;
              supplierName = expense.supplierId.companyName || "Fournisseur inconnu";
            }
            // Cas 2: supplierId est un ID string
            else if (typeof expense.supplierId === 'string') {
              supplierIdValue = expense.supplierId;
              const supplierObj = currentSuppliers.find(s => s._id === expense.supplierId);
              supplierName = supplierObj ? supplierObj.companyName : `Fournisseur #${expense.supplierId.slice(-6)}`;
            }
          }

          return {
            ...expense,
            locationId: locationId,
            locationName: locationName,
            supplierId: supplierIdValue || expense.supplierId,
            supplierName: supplierName,
            // Garder l'ID original pour la compatibilité
            location: locationId || expense.location
          };
        });

        setExpenses(processedExpenses || []);
        console.log(`✅ ${processedExpenses.length} dépenses chargées`);
      }
    } catch (err) {
      console.error("❌ Erreur chargement dépenses:", err);
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 AJOUTER UNE NOUVELLE CATÉGORIE
  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      setError("Le nom de la catégorie est requis");
      return;
    }

    const categoryName = newCategory.trim().toLowerCase().replace(/\s+/g, '_');
    const categoryLabel = newCategory.trim();

    if (categories.some(cat => cat.name === categoryName)) {
      setError("Cette catégorie existe déjà");
      return;
    }

    const newCat = {
      name: categoryName,
      label: categoryLabel,
      color: getRandomColor(),
      isDefault: false
    };

    setCategories(prev => [...prev, newCat]);
    setNewExpense(prev => ({ ...prev, category: categoryName }));
    setNewCategory("");
    setShowAddCategory(false);
    setSuccessMessage(`✅ Catégorie "${categoryLabel}" ajoutée`);

    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const getRandomColor = () => {
    const colors = [
      "#e74c3c", "#8e44ad", "#3498db", "#1abc9c",
      "#f1c40f", "#e67e22", "#95a5a6", "#7f8c8d",
      "#34495e", "#16a085", "#27ae60", "#2980b9"
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // 🔥 AJOUTER UNE NOUVELLE LOCATION
  const handleAddLocation = async () => {
    if (!newLocationName.trim()) {
      setError("Le nom de la localisation est requis");
      return;
    }

    try {
      const response = await axiosInstance.post("/api/Locationspin/addLocation", {
        name: newLocationName.trim()
      });

      if (response.data) {
        setSuccessMessage(`✅ Localisation "${newLocationName}" ajoutée`);
        setNewLocationName("");
        setShowAddLocation(false);
        await loadLocations();

        if (response.data._id) {
          setNewExpense(prev => ({ ...prev, location: response.data._id }));
          setNewSupplierExpense(prev => ({ ...prev, location: response.data._id }));
        }

        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (err) {
      console.error("❌ Erreur ajout localisation:", err);
      setError(err.response?.data?.message || "Erreur lors de l'ajout de la localisation");
    }
  };

  // 🔥 GÉRER LES CHANGEMENTS DE FORMULAIRE
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewExpense(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked :
        (type === 'number' ? (value === "" ? "" : parseFloat(value)) : value)
    }));
  };

  const handleSupplierExpenseInputChange = (e) => {
    const { name, value } = e.target;
    setNewSupplierExpense(prev => ({
      ...prev,
      [name]: name === 'amount' || name === 'remainingAmount' || name === 'priority'
        ? (value === "" ? "" : parseFloat(value))
        : value
    }));
  };

  // 🔥 CHANGEMENT DE LOCATION DANS LE FILTRE
  const handleLocationFilterChange = (e) => {
    const locationId = e.target.value;
    setSelectedLocation(locationId);
  };

  // 🔥 AJOUTER UNE NOUVELLE DÉPENSE NORMALE
  const handleAddExpense = async (e) => {
    e.preventDefault();

    if (!newExpense.name.trim()) {
      setError("Le nom de la dépense est requis");
      return;
    }

    if (!newExpense.location) {
      setError("Veuillez sélectionner une localisation");
      return;
    }

    if (!newExpense.amount || newExpense.amount <= 0) {
      setError("Le montant doit être supérieur à 0");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const expenseData = {
        name: newExpense.name.trim(),
        category: newExpense.category,
        location: newExpense.location,
        amount: parseFloat(newExpense.amount),
        priority: parseInt(newExpense.priority),
        isRecurring: newExpense.isRecurring,
        notes: newExpense.notes,
        remainingAmount: parseFloat(newExpense.amount)
      };

      // Ajouter recurrence si récurrente
      if (newExpense.isRecurring && newExpense.recurrence) {
        expenseData.recurrence = newExpense.recurrence;
      }

      console.log("📤 Envoi données dépense:", expenseData);

      const response = await axiosInstance.post("/api/Expenses/createExpense", expenseData);

      if (response.data.success) {
        setSuccessMessage(`✅ Dépense "${response.data.expense.name}" ajoutée avec succès`);

        // Réinitialiser le formulaire
        setNewExpense({
          name: "",
          category: "divers",
          location: newExpense.location, // Garder la même location
          amount: "",
          priority: 3,
          isRecurring: false,
          recurrence: "monthly",
          notes: ""
        });

        await loadAllExpenses();

        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.data.message || "Erreur lors de l'ajout");
      }

    } catch (err) {
      console.error("❌ Erreur ajout dépense:", err);
      setError(err.response?.data?.message || "Erreur lors de l'ajout");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 AJOUTER UNE NOUVELLE DÉPENSE FOURNISSEUR
  const handleAddSupplierExpense = async (e) => {
    e.preventDefault();

    if (!newSupplierExpense.supplierId) {
      setError("Veuillez sélectionner un fournisseur");
      return;
    }

    if (!newSupplierExpense.location) {
      setError("Veuillez sélectionner une localisation");
      return;
    }

    if (!newSupplierExpense.amount || newSupplierExpense.amount <= 0) {
      setError("Le montant doit être supérieur à 0");
      return;
    }

    if (newSupplierExpense.remainingAmount === "" || newSupplierExpense.remainingAmount < 0) {
      setError("Le montant restant à payer est requis");
      return;
    }

    if (parseFloat(newSupplierExpense.remainingAmount) > parseFloat(newSupplierExpense.amount)) {
      setError("Le montant restant ne peut pas être supérieur au montant total");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const selectedSupplier = suppliers.find(s => s._id === newSupplierExpense.supplierId);
      if (!selectedSupplier) {
        setError("Fournisseur non trouvé");
        return;
      }

      const expenseData = {
        name: `Facture ${selectedSupplier.companyName}`,
        category: "fournisseurs",
        location: newSupplierExpense.location,
        amount: parseFloat(newSupplierExpense.amount),
        remainingAmount: parseFloat(newSupplierExpense.remainingAmount),
        priority: parseInt(newSupplierExpense.priority),
        notes: newSupplierExpense.notes,
        supplierId: newSupplierExpense.supplierId,
        isRecurring: false
      };

      console.log("📤 Envoi données dépense fournisseur:", expenseData);

      const response = await axiosInstance.post("/api/Expenses/createExpense", expenseData);

      if (response.data.success) {
        setSuccessMessage(`✅ Dépense fournisseur "${selectedSupplier.companyName}" ajoutée avec succès`);

        setNewSupplierExpense({
          supplierId: "",
          location: newSupplierExpense.location, // Garder la même location
          amount: "",
          priority: 3,
          notes: "",
          remainingAmount: ""
        });

        await loadAllExpenses();

        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.data.message || "Erreur lors de l'ajout");
      }

    } catch (err) {
      console.error("❌ Erreur ajout dépense fournisseur:", err);
      setError(err.response?.data?.message || "Erreur lors de l'ajout");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 METTRE À JOUR UNE DÉPENSE (AJOUTER UNE LOCATION AUX ANCIENNES DÉPENSES)
  const handleUpdateExpense = async (expenseId, updates) => {
    try {
      setLoading(true);

      const response = await axiosInstance.put(
        `/api/Expenses/updateExpense/${expenseId}`,
        updates
      );

      if (response.data.success) {
        setSuccessMessage(`✅ Dépense mise à jour avec succès`);
        await loadAllExpenses();

        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.data.message);
      }

    } catch (err) {
      console.error("❌ Erreur mise à jour:", err);
      setError(err.response?.data?.message || "Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 METTRE À JOUR LES ANCIENNES DÉPENSES SANS LOCATION
  const updateOldExpensesWithLocation = async () => {
    if (!window.confirm("Voulez-vous ajouter une location par défaut aux anciennes dépenses?")) {
      return;
    }

    try {
      setLoading(true);
      setError("");

      // Trouver une location par défaut
      const defaultLocation = locations.length > 0 ? locations[0]._id : null;
      if (!defaultLocation) {
        setError("Aucune location disponible");
        return;
      }

      // Récupérer les dépenses sans location
      const expensesWithoutLocation = expenses.filter(expense =>
        !expense.locationId && !expense.location
      );

      if (expensesWithoutLocation.length === 0) {
        setSuccessMessage("✅ Toutes les dépenses ont déjà une location");
        return;
      }

      console.log(`🔄 Mise à jour de ${expensesWithoutLocation.length} dépenses sans location...`);

      // Mettre à jour chaque dépense
      for (const expense of expensesWithoutLocation) {
        try {
          await axiosInstance.put(`/api/Expenses/updateExpense/${expense._id}`, {
            location: defaultLocation
          });
          console.log(`📍 Dépense "${expense.name}" mise à jour avec location`);
        } catch (err) {
          console.error(`❌ Erreur mise à jour dépense ${expense._id}:`, err);
        }
      }

      setSuccessMessage(`✅ ${expensesWithoutLocation.length} anciennes dépenses mises à jour avec une location`);
      await loadAllExpenses();

      setTimeout(() => setSuccessMessage(""), 5000);

    } catch (err) {
      console.error("❌ Erreur mise à jour anciennes dépenses:", err);
      setError("Erreur lors de la mise à jour des anciennes dépenses");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 MODIFIER DIRECTEMENT LE "RESTANT À PAYER"
  const handleUpdateRemainingAmount = async (expenseId, newRemainingAmount) => {
    try {
      setLoading(true);

      const response = await axiosInstance.put(
        `/api/Expenses/updateExpense/${expenseId}`,
        { remainingAmount: parseFloat(newRemainingAmount) }
      );

      if (response.data.success) {
        setSuccessMessage(`✅ Montant restant mis à jour`);
        await loadAllExpenses();

        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.data.message);
      }

    } catch (err) {
      console.error("❌ Erreur mise à jour montant restant:", err);
      setError(err.response?.data?.message || "Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
      setEditingRemainingAmount(null);
    }
  };

  // 🔥 SUPPRIMER UNE DÉPENSE
  const handleDeleteExpense = async (expenseId, expenseName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la dépense "${expenseName}" ?`)) {
      return;
    }

    try {
      setLoading(true);

      const response = await axiosInstance.delete(
        `/api/Expenses/deleteExpense/${expenseId}`
      );

      if (response.data.success) {
        setSuccessMessage(`🗑️ Dépense supprimée avec succès`);
        await loadAllExpenses();

        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.data.message);
      }

    } catch (err) {
      console.error("❌ Erreur suppression:", err);
      setError(err.response?.data?.message || "Erreur lors de la suppression");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 CHARGER TOUTES LES DONNÉES
  const loadAllData = async () => {
    try {
      setLoading(true);
      console.log("🚀 Début chargement toutes les données...");

      // Charger les données de référence en premier
      const [cats, locs, supps] = await Promise.all([
        axiosInstance.get("/api/Expenses/getAllCategories"),
        axiosInstance.get("/api/Locationspin/getAllLocations"),
        axiosInstance.get("/api/Suppliers/getSuppliers")
      ]);

      if (cats.data.success) setCategories(cats.data.categories || []);
      if (locs.data) setLocations(locs.data || []);

      // Correction : Le backend Suppliers ne renvoie pas de champ "success", mais "suppliers"
      const supplierList = supps.data.suppliers || [];
      setSuppliers(supplierList);

      // Passer locs.data et supplierList directement pour éviter d'attendre la mise à jour du state
      await loadAllExpenses(locs.data || [], supplierList);

    } catch (err) {
      console.error("❌ Erreur chargement initial:", err);
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 EFFECT INITIAL
  useEffect(() => {
    console.log("🚀 AdminExpensesManager monté");
    loadAllData();
  }, []);

  // 🔥 EFFECT QUAND LA LOCATION CHANGE
  useEffect(() => {
    if (selectedLocation !== "all") {
      loadAllExpenses();
    }
  }, [selectedLocation]);

  // 🔥 FORMATER LES MONTANTS
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return "0.00 DT";
    }
    const num = parseFloat(amount);
    return isNaN(num) ? "0.00 DT" : `${num.toFixed(2)} DT`;
  };

  // 🔥 CALCULER LE POURCENTAGE PAYÉ
  const getPaidPercentage = (expense) => {
    if (!expense.amount || expense.amount === 0) return 0;
    const paid = expense.amount - (expense.remainingAmount || expense.amount);
    return (paid / expense.amount * 100).toFixed(1);
  };

  // 🔥 OBTENIR LE BADGE DE STATUT
  const getStatusBadge = (expense) => {
    if (expense.status === "paid" || (expense.remainingAmount !== undefined && expense.remainingAmount <= 0.01)) {
      return { label: "Payé", color: "#27ae60", bgColor: "#d4edda" };
    }
    return { label: "En cours", color: "#f39c12", bgColor: "#fff3cd" };
  };

  // 🔥 TROUVER LA CATÉGORIE
  const getCategory = (categoryName) => {
    const foundCategory = categories.find(c => c.name === categoryName);
    if (foundCategory) {
      return foundCategory;
    }
    return { label: categoryName, color: "#b2bec3" };
  };

  // 🔥 TROUVER UN FOURNISSEUR
  const getSupplier = (supplierId) => {
    if (!supplierId) return null;

    // Extraire l'ID si c'est un objet peuplé
    const sid = typeof supplierId === 'object' ? supplierId._id : supplierId;
    if (!sid) return null;

    return suppliers.find(s => s._id === sid);
  };

  // 🔥 TROUVER LE NOM D'UNE LOCATION - CORRIGÉ POUR LE NOUVEAU FORMAT
  // 🔥 TROUVER LE NOM D'UNE LOCATION - VERSION CORRIGÉE
  const getLocationName = (location) => {
    console.log("🔍 getLocationName appelé avec:", location);

    // Si location est undefined ou null
    if (!location) {
      return "Non spécifiée";
    }

    // Si location est "all" (filtre)
    if (location === "all") {
      return "Toutes les locations";
    }

    // Si location est un objet peuplé du backend (format idéal)
    if (location && typeof location === 'object') {
      // Cas 1: Objet avec _id et name
      if (location._id && location.name) {
        console.log(`📍 Objet peuplé: ${location.name}`);
        return location.name;
      }
      // Cas 2: Objet locationObj
      if (location.locationObj && location.locationObj.name) {
        console.log(`📍 locationObj: ${location.locationObj.name}`);
        return location.locationObj.name;
      }
    }

    // Si location est un ID string
    if (typeof location === 'string') {
      // Chercher dans la liste des locations
      const locationObj = locations.find(loc => {
        // Comparer les IDs
        if (loc._id === location) return true;
        // Si location a un objet _id à l'intérieur
        if (typeof loc === 'object' && loc._id === location) return true;
        return false;
      });

      if (locationObj) {
        console.log(`📍 Trouvée par ID: ${locationObj.name}`);
        return locationObj.name;
      }

      console.log(`⚠️ ID non trouvé dans locations: ${location}`);
      return "Location inconnue";
    }

    // Fallback
    return "Non spécifiée";
  };

  // 🔥 OBTENIR LES DÉPENSES FILTRÉES
  const normalExpenses = getNormalExpenses();
  const supplierExpenses = getSupplierExpenses();

  return (
    <div className="admin-expenses-manager">
      {/* En-tête avec sélecteur de location */}
      <div className="admin-header">
        <h1>⚙️ GESTION DES CHARGES</h1>
        <div className="header-actions">
          <a href="/performance" className="back-link">
            ↩️ Retour au tableau de bord
          </a>

          {/* Sélecteur de location - FILTRER l'affichage */}
          <div className="location-filter">
            <label htmlFor="locationFilter">📍 Filtrer par localisation:</label>
            <select
              id="locationFilter"
              value={selectedLocation}
              onChange={handleLocationFilterChange}
              disabled={loading}
            >
              <option value="all">🌍 Toutes les locations</option>
              {locations.map(loc => (
                <option key={`loc-${loc._id}`} value={loc._id}>
                  📍 {loc.name}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="add-location-btn"
              onClick={() => setShowAddLocation(true)}
              disabled={loading}
              title="Ajouter une nouvelle localisation"
            >
              +
            </button>
          </div>

          <button
            onClick={loadAllData}
            className="refresh-btn"
            disabled={loading}
          >
            {loading ? "⏳" : "🔄"} Rafraîchir
          </button>

          {/* Bouton pour corriger les anciennes dépenses */}
          {expenses.some(exp => !exp.locationId && !exp.location) && (
            <button
              onClick={updateOldExpensesWithLocation}
              className="fix-old-expenses-btn"
              disabled={loading}
              title="Ajouter une location aux anciennes dépenses"
            >
              🔧 Corriger anciennes dépenses
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="error-message">
          <p>❌ {error}</p>
          <button onClick={() => setError("")}>Fermer</button>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <p>{successMessage}</p>
          <button onClick={() => setSuccessMessage("")}>OK</button>
        </div>
      )}

      {/* Modal d'ajout de location */}
      {showAddLocation && (
        <div className="add-category-overlay" onClick={() => setShowAddLocation(false)}>
          <div className="add-category-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>📍 Ajouter une localisation</h4>
              <button
                className="close-btn"
                onClick={() => setShowAddLocation(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                placeholder="Nom de la nouvelle localisation"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleAddLocation()}
              />
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowAddLocation(false)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="save-btn"
                  onClick={handleAddLocation}
                  disabled={!newLocationName.trim()}
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Afficher la location sélectionnée */}
      {selectedLocation !== "all" && (
        <div className="location-info">
          <span className="location-badge">
            📍 Filtre: {getLocationName(selectedLocation)}
          </span>
          <span className="location-stats">
            {expenses.length} dépenses |
            Total: {formatCurrency(expenses.reduce((sum, e) => sum + (e.amount || 0), 0))}
          </span>
        </div>
      )}

      {/* ============================================
          SECTION: AJOUTER UNE NOUVELLE CHARGE NORMALE
      ============================================ */}

      <div className="add-expense-section">
        <h2>➕ Ajouter une nouvelle charge normale</h2>

        <form onSubmit={handleAddExpense} className="expense-form-inline">
          <div className="form-row">
            <div className="form-group-inline">
              <label>Nom de la charge *</label>
              <input
                type="text"
                name="name"
                value={newExpense.name}
                onChange={handleInputChange}
                placeholder="Ex: Salaires du personnel"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group-inline">
              <label>Localisation *</label>
              <select
                name="location"
                value={newExpense.location}
                onChange={handleInputChange}
                required
                disabled={loading || locations.length === 0}
              >
                <option value="">Sélectionner une location</option>
                {locations.map(loc => (
                  <option key={`loc-form-${loc._id}`} value={loc._id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group-inline">
              <label>Catégorie *</label>
              <select
                name="category"
                value={newExpense.category}
                onChange={handleInputChange}
                required
                disabled={loading}
              >
                {categories.map((cat, index) => (
                  <option key={`cat-${cat.name}-${index}`} value={cat.name}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group-inline">
              <label>Montant (DT) *</label>
              <input
                type="number"
                name="amount"
                value={newExpense.amount}
                onChange={handleInputChange}
                placeholder="0.00"
                min="0.01"
                step="0.01"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group-inline">
              <label>Priorité</label>
              <select
                name="priority"
                value={newExpense.priority}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="1">1 - Très haute</option>
                <option value="2">2 - Haute</option>
                <option value="3">3 - Moyenne</option>
                <option value="4">4 - Basse</option>
                <option value="5">5 - Très basse</option>
              </select>
            </div>

            <div className="form-group-inline checkbox-inline">
              <label>
                <input
                  type="checkbox"
                  name="isRecurring"
                  checked={newExpense.isRecurring}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                Récurrente
              </label>
            </div>

            <div className="form-actions-inline">
              <button
                type="submit"
                className="add-btn"
                disabled={loading || !newExpense.name || !newExpense.amount || !newExpense.location}
              >
                {loading ? "⏳ Envoi..." : "➕ Ajouter"}
              </button>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group-inline full-row">
              <label>Notes (optionnel)</label>
              <textarea
                name="notes"
                value={newExpense.notes}
                onChange={handleInputChange}
                placeholder="Informations supplémentaires..."
                rows="1"
                disabled={loading}
              />
            </div>
          </div>

          {/* Options récurrentes */}
          {newExpense.isRecurring && (
            <div className="recurring-options">
              <div className="form-group-inline">
                <label>Fréquence</label>
                <select
                  name="recurrence"
                  value={newExpense.recurrence}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="daily">Quotidienne</option>
                  <option value="weekly">Hebdomadaire</option>
                  <option value="monthly">Mensuelle</option>
                  <option value="yearly">Annuelle</option>
                </select>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* ============================================
          SECTION: LISTE DES CHARGES NORMALES (EN LIGNE)
      ============================================ */}

      <div className="normal-expenses-section">
        <div className="section-header-inline">
          <h2>📋 Charges normales ({normalExpenses.length})</h2>
          <div className="section-summary-inline">
            <span className="summary-item-inline">
              <strong>Total:</strong> {formatCurrency(normalExpenses.reduce((sum, e) => sum + (e.amount || 0), 0))}
            </span>
            <span className="summary-item-inline">
              <strong>Restant:</strong> {formatCurrency(normalExpenses.reduce((sum, e) => sum + (e.remainingAmount || 0), 0))}
            </span>
            <span className="summary-item-inline">
              <strong>% Payé:</strong> {(() => {
                const totalAmount = normalExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
                const totalPaid = normalExpenses.reduce((sum, e) => sum + ((e.amount || 0) - (e.remainingAmount || 0)), 0);
                return totalAmount > 0 ? `${((totalPaid / totalAmount) * 100).toFixed(1)}%` : '0%';
              })()}
            </span>
          </div>
        </div>

        {loading && normalExpenses.length === 0 ? (
          <div className="loading-message">
            <p>Chargement des charges normales...</p>
          </div>
        ) : normalExpenses.length === 0 ? (
          <div className="empty-message">
            <p>Aucune charge normale configurée pour cette location.</p>
          </div>
        ) : (
          <div className="expenses-grid">
            {normalExpenses.map((expense) => {
              const status = getStatusBadge(expense);
              const category = getCategory(expense.category);
              const paidPercentage = getPaidPercentage(expense);
              const locationDisplayName = expense.locationName || getLocationName(expense.location);

              return (
                <div key={`expense-${expense._id}`} className="expense-card">
                  <div className="expense-card-header">
                    <div className="expense-name">{expense.name}</div>
                    <div className="expense-actions">
                      <button
                        className="edit-btn-small"
                        onClick={() => setEditingExpense({ ...expense, type: 'normal' })}
                        title="Modifier"
                        disabled={loading}
                      >
                        ✏️
                      </button>
                      <button
                        className="delete-btn-small"
                        onClick={() => handleDeleteExpense(expense._id, expense.name)}
                        title="Supprimer"
                        disabled={loading}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  <div className="expense-card-content">
                    <div className="expense-info-row">
                      <span className="info-label">📍 Location:</span>
                      <span className="info-value location-name">
                        {locationDisplayName}
                      </span>
                    </div>

                    <div className="expense-info-row">
                      <span className="info-label">📂 Catégorie:</span>
                      <span
                        className="category-badge-small"
                        style={{ backgroundColor: category.color }}
                      >
                        {category.label}
                      </span>
                    </div>

                    <div className="expense-info-row">
                      <span className="info-label">💰 Montant:</span>
                      <span className="info-value amount">{formatCurrency(expense.amount)}</span>
                    </div>

                    <div className="expense-info-row">
                      <span className="info-label">📊 Restant:</span>
                      <span className={`info-value ${expense.remainingAmount <= 0.01 ? 'paid' : 'pending'}`}>
                        {expense.remainingAmount <= 0.01 ? '✓ Payé' : formatCurrency(expense.remainingAmount)}
                      </span>
                    </div>

                    <div className="expense-info-row">
                      <span className="info-label">% Payé:</span>
                      <div className="progress-container">
                        <div
                          className="progress-bar"
                          style={{ width: `${paidPercentage}%` }}
                        ></div>
                        <span className="progress-text">{paidPercentage}%</span>
                      </div>
                    </div>

                    <div className="expense-info-row">
                      <span className="info-label">🎯 Priorité:</span>
                      <span className={`priority-badge priority-${expense.priority}`}>
                        {expense.priority} - {expense.priority === 1 ? 'Très haute' :
                          expense.priority === 2 ? 'Haute' :
                            expense.priority === 3 ? 'Moyenne' :
                              expense.priority === 4 ? 'Basse' : 'Très basse'}
                      </span>
                    </div>

                    <div className="expense-info-row">
                      <span className="info-label">📝 Statut:</span>
                      <span
                        className="status-badge"
                        style={{
                          color: status.color,
                          backgroundColor: status.bgColor
                        }}
                      >
                        {status.label}
                      </span>
                    </div>

                    {expense.notes && (
                      <div className="expense-info-row">
                        <span className="info-label">📄 Notes:</span>
                        <span className="info-value notes">{expense.notes}</span>
                      </div>
                    )}

                    {expense.isRecurring && (
                      <div className="expense-info-row">
                        <span className="info-label">🔄 Type:</span>
                        <span className="info-value recurring">Récurrente</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ============================================
          SECTION: FOURNISSEURS
      ============================================ */}

      <div className="supplier-section">
        <div className="section-separator">
          <hr />
          <h2 className="separator-title">📦 FOURNISSEURS</h2>
          <hr />
        </div>

        {/* Statistiques fournisseurs */}
        <div className="suppliers-stats">
          <div className="stat-card">
            <div className="stat-title">Fournisseurs</div>
            <div className="stat-value">{suppliers.length}</div>
          </div>

          <div className="stat-card">
            <div className="stat-title">Factures en cours</div>
            <div className="stat-value">{supplierExpenses.length}</div>
          </div>

          <div className="stat-card">
            <div className="stat-title">Dette fournisseurs</div>
            <div className="stat-value">
              {formatCurrency(supplierExpenses.reduce((sum, e) => sum + (e.remainingAmount || 0), 0))}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-title">% Payé fournisseurs</div>
            <div className="stat-value">
              {(() => {
                const totalAmount = supplierExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
                const totalPaid = supplierExpenses.reduce((sum, e) => sum + ((e.amount || 0) - (e.remainingAmount || 0)), 0);
                return totalAmount > 0 ? `${((totalPaid / totalAmount) * 100).toFixed(1)}%` : '0%';
              })()}
            </div>
          </div>
        </div>

        {/* Formulaire facture fournisseur */}
        <div className="add-supplier-expense-form">
          <h3>➕ Ajouter une facture fournisseur</h3>
          <form onSubmit={handleAddSupplierExpense}>
            <div className="form-grid">
              <div className="form-group">
                <label>Sélectionner un fournisseur *</label>
                <select
                  name="supplierId"
                  value={newSupplierExpense.supplierId}
                  onChange={handleSupplierExpenseInputChange}
                  required
                  disabled={loading || suppliers.length === 0}
                >
                  <option value="">Choisir un fournisseur</option>
                  {suppliers.map(supplier => (
                    <option key={`supplier-${supplier._id}`} value={supplier._id}>
                      {supplier.companyName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Localisation *</label>
                <select
                  name="location"
                  value={newSupplierExpense.location}
                  onChange={handleSupplierExpenseInputChange}
                  required
                  disabled={loading || locations.length === 0}
                >
                  <option value="">Sélectionner une location</option>
                  {locations.map(loc => (
                    <option key={`supplier-loc-${loc._id}`} value={loc._id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Montant total (DT) *</label>
                <input
                  type="number"
                  name="amount"
                  value={newSupplierExpense.amount}
                  onChange={handleSupplierExpenseInputChange}
                  placeholder="0.00"
                  min="0.01"
                  step="0.01"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Reste à payer (DT) *</label>
                <input
                  type="number"
                  name="remainingAmount"
                  value={newSupplierExpense.remainingAmount}
                  onChange={handleSupplierExpenseInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Priorité (1-5)</label>
                <select
                  name="priority"
                  value={newSupplierExpense.priority}
                  onChange={handleSupplierExpenseInputChange}
                  disabled={loading}
                >
                  <option value="1">1 - Très haute</option>
                  <option value="2">2 - Haute</option>
                  <option value="3">3 - Moyenne</option>
                  <option value="4">4 - Basse</option>
                  <option value="5">5 - Très basse</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label>Notes (optionnel)</label>
                <textarea
                  name="notes"
                  value={newSupplierExpense.notes}
                  onChange={handleSupplierExpenseInputChange}
                  placeholder="Numéro de facture, date d'échéance..."
                  rows="2"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="add-btn"
                disabled={loading || !newSupplierExpense.supplierId || !newSupplierExpense.amount ||
                  !newSupplierExpense.location || newSupplierExpense.remainingAmount === ""}
              >
                {loading ? "⏳ Envoi..." : "➕ Ajouter la facture"}
              </button>
            </div>
          </form>
        </div>

        {/* TABLEAU FACTURES FOURNISSEURS */}
        <div className="supplier-expenses-list-section">
          <div className="section-header">
            <h2>📄 Factures fournisseurs ({supplierExpenses.length})</h2>
          </div>

          {loading && supplierExpenses.length === 0 ? (
            <div className="loading-message">
              <p>Chargement des factures fournisseurs...</p>
            </div>
          ) : supplierExpenses.length === 0 ? (
            <div className="empty-message">
              <p>Aucune facture fournisseur enregistrée.</p>
            </div>
          ) : (
            <div className="supplier-expenses-table-container">
              <table className="supplier-expenses-table">
                <thead>
                  <tr>
                    <th>FOURNISSEUR</th>
                    <th>LOCATION</th>
                    <th>MONTANT TOTAL</th>
                    <th>RESTANT À PAYER</th>
                    <th>% PAYÉ</th>
                    <th>PRIORITÉ</th>
                    <th>STATUT</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierExpenses.map((expense) => {
                    const status = getStatusBadge(expense);
                    const paidPercentage = getPaidPercentage(expense);
                    const supplier = getSupplier(expense.supplierId);
                    const isEditing = editingRemainingAmount === expense._id;

                    return (
                      <tr key={`supplier-expense-${expense._id}`} className={expense.status === "paid" ? "paid-expense" : ""}>
                        <td className="supplier-name-cell">
                          {expense.supplierName && expense.supplierName !== "Non spécifié" ? (
                            <div className="supplier-info">
                              <div className="supplier-name">
                                <strong>{expense.supplierName}</strong>
                              </div>
                            </div>
                          ) : supplier ? (
                            <div className="supplier-info">
                              <div className="supplier-name">
                                <strong>{supplier.companyName}</strong>
                              </div>
                            </div>
                          ) : (
                            <div className="supplier-info">
                              <div className="supplier-name">
                                <strong>Fournisseur #{typeof expense.supplierId === 'string' ? expense.supplierId.slice(-6) : 'Inconnu'}</strong>
                              </div>
                            </div>
                          )}
                        </td>

                        <td className="location-cell">
                          <span className="location-badge-small">
                            📍 {expense.locationName || getLocationName(expense.location)}
                          </span>
                        </td>

                        <td className="amount-cell">
                          <strong>{formatCurrency(expense.amount)}</strong>
                        </td>

                        <td className="remaining-cell editable-cell">
                          {isEditing ? (
                            <div className="inline-edit-form">
                              <input
                                type="number"
                                value={tempRemainingAmount}
                                onChange={(e) => setTempRemainingAmount(e.target.value)}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                                max={expense.amount}
                                autoFocus
                                className="inline-edit-input"
                              />
                              <div className="inline-edit-actions">
                                <button
                                  className="save-inline-btn"
                                  onClick={() => {
                                    const newAmount = parseFloat(tempRemainingAmount);
                                    if (newAmount >= 0 && newAmount <= expense.amount) {
                                      handleUpdateRemainingAmount(expense._id, newAmount);
                                    } else {
                                      setError("Le montant doit être entre 0 et " + expense.amount);
                                      setEditingRemainingAmount(null);
                                    }
                                  }}
                                  disabled={loading}
                                >
                                  ✓
                                </button>
                                <button
                                  className="cancel-inline-btn"
                                  onClick={() => {
                                    setEditingRemainingAmount(null);
                                    setTempRemainingAmount("");
                                  }}
                                  disabled={loading}
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          ) : expense.remainingAmount <= 0.01 ? (
                            <span className="fully-paid">✓ Payé</span>
                          ) : (
                            <div
                              className="editable-remaining-amount"
                              onClick={() => {
                                setEditingRemainingAmount(expense._id);
                                setTempRemainingAmount(expense.remainingAmount);
                              }}
                              title="Cliquer pour modifier"
                            >
                              <span className="remaining-amount">
                                {formatCurrency(expense.remainingAmount)}
                              </span>
                              <span className="edit-icon">✏️</span>
                            </div>
                          )}
                        </td>

                        <td className="percentage-cell">
                          <div className="percentage-bar-container">
                            <div
                              className="percentage-bar"
                              style={{ width: `${paidPercentage}%` }}
                            ></div>
                            <span className="percentage-text">{paidPercentage}%</span>
                          </div>
                        </td>

                        <td className="priority-cell">
                          <span className={`priority-badge priority-${expense.priority}`}>
                            {expense.priority}
                          </span>
                        </td>

                        <td className="status-cell">
                          <span
                            className="status-badge"
                            style={{
                              color: status.color,
                              backgroundColor: status.bgColor
                            }}
                          >
                            {status.label}
                          </span>
                        </td>

                        <td className="actions-cell">
                          <div className="action-buttons">
                            <button
                              className="edit-btn"
                              onClick={() => setEditingExpense({ ...expense, type: 'supplier' })}
                              title="Modifier"
                              disabled={loading}
                            >
                              ✏️
                            </button>

                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteExpense(expense._id, supplier ? `Facture ${supplier.companyName}` : "Cette facture")}
                              title="Supprimer"
                              disabled={loading}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal d'édition */}
      {editingExpense && (
        <div className="edit-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>✏️ Modifier la dépense</h3>
              <button
                className="close-btn"
                onClick={() => setEditingExpense(null)}
                disabled={loading}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Nom</label>
                <input
                  type="text"
                  value={editingExpense.name}
                  onChange={(e) => setEditingExpense({
                    ...editingExpense,
                    name: e.target.value
                  })}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Localisation</label>
                <select
                  value={editingExpense.location?._id || editingExpense.location || ""}
                  onChange={(e) => setEditingExpense({
                    ...editingExpense,
                    location: e.target.value
                  })}
                  disabled={loading}
                >
                  <option value="">Sélectionner une location</option>
                  {locations.map(loc => (
                    <option key={`edit-loc-${loc._id}`} value={loc._id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>

              {editingExpense.supplierId ? (
                <div className="form-group">
                  <label>Fournisseur</label>
                  <input
                    type="text"
                    value={getSupplier(editingExpense.supplierId)?.companyName || (typeof editingExpense.supplierId === 'object' ? editingExpense.supplierId.companyName : "Fournisseur")}
                    disabled
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>Catégorie</label>
                  <select
                    value={editingExpense.category}
                    onChange={(e) => setEditingExpense({
                      ...editingExpense,
                      category: e.target.value
                    })}
                    disabled={loading}
                  >
                    {categories.map((cat, index) => (
                      <option key={`edit-cat-${cat.name}-${index}`} value={cat.name}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Montant (DT)</label>
                <input
                  type="number"
                  value={editingExpense.amount}
                  onChange={(e) => setEditingExpense({
                    ...editingExpense,
                    amount: parseFloat(e.target.value) || 0
                  })}
                  min="0"
                  step="0.01"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Montant restant (DT)</label>
                <input
                  type="number"
                  value={editingExpense.remainingAmount}
                  onChange={(e) => setEditingExpense({
                    ...editingExpense,
                    remainingAmount: parseFloat(e.target.value) || 0
                  })}
                  min="0"
                  step="0.01"
                  max={editingExpense.amount}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Priorité</label>
                <select
                  value={editingExpense.priority}
                  onChange={(e) => setEditingExpense({
                    ...editingExpense,
                    priority: parseInt(e.target.value)
                  })}
                  disabled={loading}
                >
                  <option value="1">1 - Très haute</option>
                  <option value="2">2 - Haute</option>
                  <option value="3">3 - Moyenne</option>
                  <option value="4">4 - Basse</option>
                  <option value="5">5 - Très basse</option>
                </select>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={editingExpense.notes || ""}
                  onChange={(e) => setEditingExpense({
                    ...editingExpense,
                    notes: e.target.value
                  })}
                  rows="3"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setEditingExpense(null)}
                disabled={loading}
              >
                Annuler
              </button>

              <button
                className="save-btn"
                onClick={() => {
                  handleUpdateExpense(editingExpense._id, {
                    name: editingExpense.name,
                    category: editingExpense.category,
                    location: editingExpense.location?._id || editingExpense.location,
                    amount: editingExpense.amount,
                    remainingAmount: editingExpense.remainingAmount,
                    priority: editingExpense.priority,
                    notes: editingExpense.notes
                  });
                  setEditingExpense(null);
                }}
                disabled={loading}
              >
                {loading ? "⏳ Sauvegarde..." : "💾 Sauvegarder"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* État de chargement */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Chargement...</p>
        </div>
      )}
    </div>
  );
}

export default AdminExpensesManager;
